import { FC, Fragment } from "preact/compat";
import { useEffect, useMemo } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import { intervalToDuration } from "date-fns";
import { ExternalTile, TaskTile, TrackGroupTile, useApp } from "~/core";
import { Button, IconButton, SingleTime, TimeDistance } from "~/components";
import { useDocumentPiP } from "~/hooks";
import { leadZero } from "~/utils";
import { DiscoveryItem } from "../DiscoveryItem";
import { DocumentPictureInPicture } from "../DocumentPictureInPicture";
import TrackerPiP from "./TrackerPiP";
import ExpandMoreIcon from "@material-design-icons/svg/outlined/expand_more.svg";
import FastRewindIcon from "@material-design-icons/svg/outlined/fast_rewind.svg";
import OpenIcon from "@material-design-icons/svg/outlined/open_in_new.svg";
import CloseIcon from "@material-design-icons/svg/outlined/open_in_new_off.svg";
import PauseIcon from "@material-design-icons/svg/outlined/pause.svg";
import PlayIcon from "@material-design-icons/svg/outlined/play_arrow.svg";
import ScheduleIcon from "@material-design-icons/svg/outlined/schedule.svg";
import WarningIcon from "@material-design-icons/svg/outlined/warning.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const EACH_SECOND = 1000;
const PIP_WIDTH = 480;
const PIP_HEIGHT = 200;

const getMinStartAt = () => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);

    return date;
};

const Tracker: FC = () => {
    const store = useApp();
    const storeTasks = store.tracker.tasks.value;
    const storeTiles = store.tracker.tiles.value;
    const activeTask = store.tracker.activeTask.value;
    const activeTile = store.tracker.activeTile.value;
    const activeExternal = store.tracker.activeExternal.value;
    const { loading, load, stop, startTask, startTile } = store.tracker;
    const { isSupported, pipWindow, request, close } = useDocumentPiP();
    // @TODO: think about it (uniq by)
    const tasks = storeTasks.sort(
        (a, b) => b.startDate.getTime() - a.startDate.getTime(),
    );
    const tiles = useMemo(() => {
        const result: (ExternalTile | TrackGroupTile)[] = storeTiles.sort(
            (a, b) =>
                Number(a.inactive ?? 0) - Number(b.inactive ?? 0) ||
                a.id.localeCompare(b.id),
        );

        if (activeExternal) {
            result.unshift(activeExternal);
        }

        return result;
    }, [storeTiles, activeExternal]);
    const isExpanded = useSignal(false);
    const currentActivity = activeTask || activeTile || activeExternal;
    const taskNameText = currentActivity?.name ?? "No active task";

    useEffect(() => {
        load();

        const handleMouseDown = (event: MouseEvent) => {
            if (
                !isExpanded.peek() ||
                !event.target ||
                !(event.target instanceof Element)
            ) {
                return;
            }

            const tag = "header.tracker";

            if (
                !event.target.closest("[data-tracker]") &&
                !event.target.closest("[data-backdrop]") &&
                !event.target.closest(`[data-discovery-container="${tag}"]`)
            ) {
                isExpanded.value = false;
            }
        };

        window.addEventListener("mousedown", handleMouseDown);

        return () => {
            window.removeEventListener("mousedown", handleMouseDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const startDate = currentActivity?.startDate;
        if (!startDate) {
            return;
        }

        const updateTitle = () => {
            const duration = intervalToDuration({
                start: startDate,
                end: new Date(),
            });

            store.meta.prefix.value = `(${leadZero(duration.hours)}:${leadZero(
                duration.minutes,
            )})`;
        };

        const intervalId = window.setInterval(updateTitle, 1000 * 60);

        updateTitle();

        return () => {
            store.meta.prefix.value = "";
            window.clearInterval(intervalId);
        };
    }, [currentActivity, store.meta.prefix]);

    const handlePlay = async () => {
        if (currentActivity?._track) {
            stop(currentActivity?._track);
        } else if (tasks.length > 0) {
            const task = tasks[0];

            if (task._task) {
                startTask(task._task);
            }
        } else if (!loading.value) {
            store.snack.info("No recent tasks", "You don't have any tasks");
        }
    };

    const handleClickTile = async (
        tile: TrackGroupTile | TaskTile | ExternalTile,
    ) => {
        if (currentActivity?.id === tile.id) {
            return;
        }

        if (currentActivity?._track) {
            await stop(currentActivity._track);
        }

        if (tile.inactive) {
            store.snack.warning(
                "Cannot start this track",
                "Some of the fields in this track are no longer active",
            );
            return;
        }

        if (tile.type === "track_group") {
            await startTile(tile._trackGroup);
        } else if (tile.type === "task" && tile._task) {
            await startTask(tile._task);
        }
        //
        // external cannot be started directly from the tracker view

        isExpanded.value = false;
    };

    const handleChangeStart = async (date: Date) => {
        if (
            !currentActivity ||
            !currentActivity._track ||
            currentActivity.startDate?.getTime() === date.getTime()
        ) {
            return;
        }

        try {
            await store.api.time.track.update({
                id: currentActivity._track,
                start: date,
            });
        } catch (err: unknown) {
            console.log("[tracker play back]: error happened", err);
            store.snack.error(
                "Error happened",
                "Couldn't change track start time",
            );
        }
    };

    const handlePlay5MinutesBack = () => {
        if (!currentActivity || !currentActivity.startDate) {
            return;
        }

        const newDate = new Date(currentActivity.startDate);

        newDate.setMinutes(newDate.getMinutes() - 5);

        handleChangeStart(newDate);
    };

    const handlePiP = () => {
        if (pipWindow.peek()) {
            close();
        } else if (isSupported) {
            request(PIP_WIDTH, PIP_HEIGHT);
        }
    };

    return (
        <div className="absolute right-2 z-[100] hidden md:block">
            {pipWindow.value && (
                <DocumentPictureInPicture>
                    <TrackerPiP
                        onPlay={handlePlay}
                        onTileClick={handleClickTile}
                    />
                </DocumentPictureInPicture>
            )}
            <div
                className="relative"
                data-tracker
                data-discovery-target="v=transparent"
            >
                <div
                    className={cn(
                        "flex w-[340px] items-center justify-between rounded-[20px] bg-[#335370] fill-current px-3 py-1 text-white transition-[border-radius] duration-100",
                        isExpanded.value && "rounded-b-none",
                    )}
                >
                    <div className="flex items-center">
                        <DiscoveryItem
                            tag="header.tracker"
                            inline
                            flat
                            className="mr-2"
                            onOpen={() => (isExpanded.value = true)}
                            onClose={() => (isExpanded.value = false)}
                            maxWidth={600}
                            maxHeight={400}
                            popoverOffset={{
                                left: -620,
                                top: 40,
                            }}
                            buttonClassName="text-white mt-1"
                        />
                        {loading.value ? (
                            <div className="p-0.5">
                                <Loader
                                    width="20"
                                    height="20"
                                    className="text-sky-500"
                                />
                            </div>
                        ) : (
                            <button
                                type="button"
                                title={currentActivity ? "Pause" : "Start"}
                                onClick={handlePlay}
                                className="flex rounded-full p-0.5 outline-none transition duration-100 focus:ring-2 focus:ring-white active:scale-95"
                            >
                                {currentActivity ? (
                                    <PauseIcon width="20" height="20" />
                                ) : (
                                    <PlayIcon width="20" height="20" />
                                )}
                            </button>
                        )}
                        <div className="ml-2">
                            {currentActivity?.startDate && (
                                <TimeDistance
                                    date={currentActivity.startDate}
                                    className="cursor-default truncate"
                                    period={EACH_SECOND}
                                    withSeconds
                                />
                            )}
                        </div>
                        <div className="ml-2 max-w-[180px] truncate">
                            {taskNameText}
                        </div>
                    </div>
                    <div>
                        <button
                            title="Expand"
                            type="button"
                            onClick={() =>
                                (isExpanded.value = !isExpanded.value)
                            }
                            data-journey-pass
                            className="flex rounded-full p-1 outline-none transition duration-100 focus:ring-2 focus:ring-white"
                        >
                            <ExpandMoreIcon
                                className={cn(
                                    "transition duration-100",
                                    isExpanded.value && "rotate-180 transform",
                                )}
                                width="20"
                                height="20"
                            />
                        </button>
                    </div>
                </div>
                <Transition
                    show={isExpanded.value}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0"
                    enterTo="transform opacity-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100"
                    leaveTo="transform opacity-0"
                    as={Fragment}
                >
                    <div className="absolute left-0 top-full w-full">
                        <div className="h-[7px] w-full bg-[#335370]" />
                        <div className="overflow-y-overlay h-[calc(100vh-50px)] w-full border-x border-gray-300 bg-white p-4">
                            {isSupported && (
                                <div className="mb-4 flex justify-center border-b border-gray-200 pb-4">
                                    <Button
                                        className="flex items-center fill-current"
                                        onClick={handlePiP}
                                    >
                                        <span className="mr-4">
                                            {pipWindow.value ? "Close" : "Open"}{" "}
                                            Picture-In-Picture
                                        </span>
                                        {pipWindow.value ? (
                                            <CloseIcon width="20" height="20" />
                                        ) : (
                                            <OpenIcon width="20" height="20" />
                                        )}
                                    </Button>
                                </div>
                            )}
                            <div>
                                {tiles.map((tile) => (
                                    <button
                                        key={tile.id}
                                        type="button"
                                        className={cn(
                                            "mb-4 flex w-full flex-col rounded-lg border bg-white fill-current px-4 py-2",
                                            currentActivity?.id === tile.id
                                                ? "cursor-default border-sky-300"
                                                : "border-gray-300",
                                        )}
                                        onClick={() => handleClickTile(tile)}
                                    >
                                        <div
                                            className={cn(
                                                "flex w-full items-center",
                                                currentActivity?.id === tile.id
                                                    ? "text-sky-500"
                                                    : "hover:text-sky-600",
                                            )}
                                        >
                                            <span className="mr-4 flex h-[46px] w-[46px] min-w-[46px] items-center justify-center rounded-full bg-gradient-to-br from-sky-300 to-purple-300 shadow-inner-bold">
                                                <ScheduleIcon
                                                    width="18"
                                                    height="18"
                                                    className="fill-white"
                                                />
                                            </span>
                                            <span className="text-left font-bold">
                                                {tile.name}
                                            </span>
                                            {tile.inactive && (
                                                <div
                                                    title="This track cannot be started"
                                                    className="ml-2 fill-amber-500"
                                                >
                                                    <WarningIcon
                                                        width="20"
                                                        height="20"
                                                        className="min-w-5"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {currentActivity?.id === tile.id && (
                                            <div className="mt-1 flex items-center">
                                                <IconButton
                                                    title="Start 5 minutes earlier"
                                                    className="mr-4"
                                                    onClick={
                                                        handlePlay5MinutesBack
                                                    }
                                                >
                                                    <FastRewindIcon
                                                        width="18"
                                                        height="18"
                                                    />
                                                </IconButton>
                                                <span className="mr-2">
                                                    Started at
                                                </span>
                                                <SingleTime
                                                    min={getMinStartAt()}
                                                    suggestions={[]}
                                                    value={
                                                        currentActivity.startDate ||
                                                        new Date()
                                                    }
                                                    onChange={handleChangeStart}
                                                />
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    );
};

export default Tracker;
